import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { ConstValues } from '@openx/types/helpers.ts';
import { useTranslation } from 'react-i18next';
import { getChipContainerStyles, getOperatorChipStyles } from './utils/getChipStyles.ts';
import { getOperatorText } from './utils/getOperatorText.ts';
export type OperatorType = ConstValues<typeof OperatorType>;
export const OperatorType = {
  AND: 'AND',
  OR: 'OR',
  EXCLUDE: 'EXCLUDE',
} as const;

type BaseProps = {
  operator: OperatorType;
  operatorText?: string;
  dataTest?: string;
  hideDivider?: boolean;
  disabled?: boolean;
  loading?: boolean;
};

type WithoutReadOnly = {
  readonly?: false;
  onOperatorChange: () => void;
};

type WithReadOnly = {
  readonly: true;
};

export type OperatorChipProps = BaseProps & (WithReadOnly | WithoutReadOnly);

export const OperatorChip = ({
  operator,
  disabled = false,
  loading = false,
  operatorText: customOperatorText,
  dataTest,
  hideDivider,
  ...rest
}: OperatorChipProps) => {
  const { t } = useTranslation();

  const operatorText = customOperatorText || getOperatorText(operator, t);

  return (
    <Box sx={getChipContainerStyles(hideDivider)}>
      <Button
        variant="outlined"
        data-test={dataTest}
        disabled={rest.readonly || disabled || loading}
        sx={{
          ...getOperatorChipStyles(operator, hideDivider),
          ...(rest.readonly && {
            pointerEvents: 'none',
            '&:hover': {
              cursor: 'default',
            },
          }),
        }}
        onClick={rest.readonly ? undefined : rest.onOperatorChange}
      >
        {operatorText}
      </Button>

      {disabled && (
        <Tooltip
          data-test="operator-selector-tooltip"
          title={t('Intersection unavailable - audience used in deal leveraging percentage of media fee.')}
        >
          <IconButton size="large">
            <InfoRoundedIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
